import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import HtmlContent from '../Html';
import { PROPERTY_URLS } from '../../urls';
import { Kfh_AreaType, Kfh_RegionType } from '../../types/graphqlTypes';
import { PageBackButton } from '../PageBackButton';
import { Section } from '../Section';
import { LocationNavigation } from './LocationNavigation';
import { Typography } from '../Typography';
import { SideAndMain, StyledSide } from '../layout/SideAndMain';
import { AverageSoldPricesByPeriod } from './AverageSoldPricesByPeriod';
import { AverageSoldPricesLastYear } from './AverageSoldPricesLastYear';
import { AverageSoldPricesHistorical } from './AverageSoldPricesHistorical';
import { AverageSoldPricesInRegion } from './AverageSoldPricesInRegion';
import { Divider } from '../Divider';
import { Spacer } from '../Spacer';
import { IconLink } from '../IconLink';

interface LocationSoldDataProps {
  location: Kfh_AreaType | Kfh_RegionType;
}

const StyledSoldPropertyLinks = styled.div`
  ${({ theme: { borders, space, media } }) => css`
    border-top: ${borders.default};
    border-bottom: ${borders.default};
    padding: ${space.lg} 0;
    display: grid;
    gap: ${space.sm};

    // Tablet > styles

    @media (min-width: ${media.md}) {
      grid-column-gap: ${space.lg};
      grid-template-columns: repeat(2, 1fr);
    }
  `};
`;

const LocationSoldData: FC<LocationSoldDataProps> = ({ location }) => {
  const { relativeUrl, seoPages, name } = location;
  const region = 'region' in location ? location?.region : undefined;
  const areas = region ? region.relatedAreas?.edges : (location as Kfh_RegionType)?.relatedAreas?.edges;
  const soldData = location?.locationSoldData?.soldData;
  const seo = seoPages.find((seoPage) => seoPage.remoteTypeName === 'KfhSeoAreaGuideSoldType');

  // Categorisation

  const isArea = !!region;

  // Sold prices by region

  return (
    <>
      {/* Back button */}

      <PageBackButton url={relativeUrl} label={`${name} overview`} />

      <Section contained>
        <SideAndMain>
          {/* Main content */}
          <Spacer responsive>
            <Typography component="h1" variant="displaySmall">
              {seo?.h1 ? seo?.h1 : `${name} house prices and property data`}
            </Typography>

            {/* Annual summary */}

            <HtmlContent>
              <h2>Annual summary</h2>
              <Typography component="p" variant="lead">
                The London property market is dynamic, and prices differ between areas, and over time.
              </Typography>
              <p>
                If you are looking to understand what is happening in your local area today,{' '}
                <Link to="/contact-us">get in touch with one of our experts</Link> who can discuss the current market or
                provide an up to date valuation for your property.
              </p>
              <p>The following is an annual summary based on Land Registry data.</p>
              {soldData?.summary && <p>{soldData.summary}</p>}
              {soldData?.summary && <p>{soldData.summary2}</p>}
            </HtmlContent>

            {/* Sold/Let property link */}

            <StyledSoldPropertyLinks>
              <IconLink
                arrowRight
                url={`${relativeUrl}${PROPERTY_URLS.propertiesForSale}`}
                label={`Properties for sale in ${name}`}
              />
              <IconLink
                arrowRight
                url={`${relativeUrl}${PROPERTY_URLS.propertiesSold}`}
                label={`Sold properties in ${name}`}
              />
              <IconLink
                arrowRight
                url={`${relativeUrl}${PROPERTY_URLS.propertiesForRent}`}
                label={`Properties for rent in ${name}`}
              />
              <IconLink
                arrowRight
                url={`${relativeUrl}${PROPERTY_URLS.propertiesLet}`}
                label={`Let properties in ${name}`}
              />
            </StyledSoldPropertyLinks>

            {/* Average Sold prices by period */}

            <AverageSoldPricesByPeriod monthlyStatistics={soldData?.monthlyStatistics} locationName={name} />

            <Divider />

            {/* Average Sold prices last year */}

            <AverageSoldPricesLastYear
              locationName={name}
              lastYearMonthlyStatistics={soldData?.lastYearMonthlyStatistics}
            />

            <Divider />

            {/* Average Sold prices historically */}

            <AverageSoldPricesHistorical locationName={name} yearlyAverage={soldData?.yearlyAverage} />

            <Divider />

            {/* Average Sold prices in region */}
            <AverageSoldPricesInRegion
              regionName={region ? region.name : name}
              locationName={name}
              regionSoldPrices={
                region
                  ? region.locationSoldDataChange?.soldDataChanges?.averages
                  : location.locationSoldDataChange?.soldDataChanges?.averages
              }
              areas={areas}
            />
          </Spacer>
          {/* Side navigation */}
          <StyledSide>
            <LocationNavigation url={relativeUrl} name={name} isArea={isArea} />
          </StyledSide>
        </SideAndMain>
      </Section>
    </>
  );
};

export default LocationSoldData;
